import React from 'react';
import { Paper, Tabs, Tab, Box, CircularProgress } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { SectionTabsProps } from '../../types/lessonPlan.types';

// Extended props to include streaming states
interface ExtendedSectionTabsProps extends SectionTabsProps {
  streamingStates?: {
    [key: string]: {
      isActive: boolean;
      progress: number;
      wordsPerMinute?: number;
      estimatedCompletion?: number;
    };
  };
}

/**
 * SectionTabs Component
 * 
 * Provides tabbed navigation between Introduction and Independent Practice sections.
 * Tabs show visual indicators when content has been generated successfully.
 * Shows circular progress when content is being generated.
 */
const SectionTabs: React.FC<ExtendedSectionTabsProps> = ({
  activeTab,
  onTabChange,
  sections,
  disabled = false,
  streamingStates = {}
}) => {
  // Check if introduction section is generating
  const isIntroGenerating = streamingStates['intro-story']?.isActive || streamingStates['intro-activities']?.isActive;
  
  // Check if independent section is generating  
  const isIndependentGenerating = streamingStates['practice-story']?.isActive || streamingStates['practice-activities']?.isActive;

  return (
    <Paper elevation={0} sx={{ mb: 3 }}>
      <Tabs 
        value={activeTab} 
        onChange={(_, newValue) => onTabChange(newValue)}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tab 
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <SchoolIcon />
              <span>Introduction Section</span>
              {isIntroGenerating && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </Box>
          }
          disabled={disabled}
          sx={{ 
            textTransform: 'none',
            color: sections.introduction.isGenerated ? 'success.main' : 'text.secondary',
            '&.Mui-selected': {
              color: sections.introduction.isGenerated ? 'success.main' : 'primary.main',
            },
            py: 2,
          }}
        />
        <Tab 
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <AssignmentIcon />
              <span>Independent Practice</span>
              {isIndependentGenerating && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </Box>
          }
          disabled={disabled}
          sx={{ 
            textTransform: 'none',
            color: sections.independent.isGenerated ? 'success.main' : 'text.secondary',
            '&.Mui-selected': {
              color: sections.independent.isGenerated ? 'success.main' : 'primary.main',
            },
            py: 2,
          }}
        />
      </Tabs>
    </Paper>
  );
};

export default SectionTabs; 